import type { MapCreatorGeneratedDecorationKind } from "./lib/map-creator-generated-props";

export type Id = string;
export type SceneType = "map" | "image" | "video" | "story" | "color" | "poker" | "broadside";
export type CampaignKind = "tabletop" | "poker" | "broadside";
export type AssetKind = "map" | "image" | "video" | "audio" | "token" | "original" | "mask";
export type AudioCategory = "ambience" | "sfx" | "music";
export type TokenType = "player" | "enemy" | "neutral";
export type CharacterSheetProfileId = "daring-destiny" | "paths-frontiers" | "warfront-fantasy" | "chronicles-calamity" | "chrome-pulse-rebellion" | "veil-midnight" | "shadow-runners" | "blades-dusk" | "shattered-worlds";
export type CharacterSheetKind = "character" | "npc";
export type HeroSeatSlot = "left" | "center" | "right";
export type TreeSpecies = "broadleaf" | "conifer";
export type TreeDensity = "sparse" | "normal" | "dense";
export type TwirlStyle = "dust" | "arcane" | "water" | "shadow";
export type TwirlIntensity = "gentle" | "strong" | "violent";
export type TwirlDirection = "clockwise" | "counterclockwise";
export type VolumeFireForm = "torch" | "bonfire" | "brazier";
export type VolumeFireIntensity = "low" | "medium" | "high";
export type VolumeFireSmoke = "clean" | "normal" | "heavy";

export interface TreeSettings {
  species: TreeSpecies;
  density: TreeDensity;
}
export interface TwirlSettings {
  style: TwirlStyle;
  intensity: TwirlIntensity;
  direction: TwirlDirection;
}
export interface VolumeFireSettings {
  form: VolumeFireForm;
  intensity: VolumeFireIntensity;
  smoke: VolumeFireSmoke;
}
export type PortalState = "open" | "closed" | "locked" | "secret";
export type AtmosphericFogLevel = "off" | "light" | "medium" | "heavy";
export type RainLevel = "off" | "light" | "medium" | "heavy";
export type WaterType = "ocean" | "river";
export type LightningLevel = "off" | "distant" | "storm" | "severe";
export type SnowLevel = "off" | "light" | "medium" | "heavy";
export type SnowflakeSize = "small" | "medium" | "large" | "mixed";
export type WindSpeed = "none" | "normal" | "moderate" | "high";
export type TimeOfDay = "night" | "morning" | "afternoon" | "evening";
export type EnvironmentMaterial = "ground" | "vegetation" | "wood" | "stone" | "metal" | "ice" | "snow" | "mud" | "lava";
export type EnvironmentSoundEvent = "rain" | "wind" | "water" | "fire";
export type FogTool = "reveal-brush" | "reveal-rect" | "reveal-polygon" | "conceal-brush" | "hard-hide";
export type EditorTool = "select" | "pan" | "player-view" | "wall" | "wall-freeform" | "portal" | "light" | "token" | "water" | "grass" | "material" | "weather-dry" | "physical-object" | "measure" | "template-circle" | "template-cone" | "template-rectangle" | "map-pin" | "ping" | FogTool;
export type SceneLayerKey = "background" | "terrain" | "tokens" | "foreground" | "lighting" | "fog" | "gm";
export type TokenLayer = "tokens" | "foreground" | "gm";
export type StatusMarker = "injured" | "poisoned" | "prone" | "hidden" | "concentrating";
export type LightBehavior = "steady" | "flame";

export interface Point { x: number; y: number }
export interface Segment { id: Id; a: Point; b: Point; locked?: boolean }

export interface Portal extends Segment {
  state: PortalState;
  kind: "door" | "window" | "secret";
  label?: string;
  /** Windows pass sight by default; doors and secret portals block it while closed. */
  blocksSight?: boolean;
}

export interface Light {
  id: Id;
  position: Point;
  radius: number;
  color: string;
  intensity: number;
  enabled: boolean;
  /** Steady is the legacy/default light; flame adds a smooth irregular shimmer. */
  behavior?: LightBehavior;
  locked?: boolean;
}

export interface GridDefinition {
  size: number;
  type: "square" | "hex";
  color: string;
  opacity: number;
  offset: Point;
}

export interface MapGeometry {
  width: number;
  height: number;
  grid: GridDefinition;
  walls: Segment[];
  objectWalls: Segment[];
  portals: Portal[];
  lights: Light[];
  ambientLight: number;
  source: {
    format: "dd2vtt" | "uvtt" | "df2vtt" | "foundry" | "image" | "generated";
    version?: string;
    originalOrigin?: Point;
    pixelsPerGrid?: number;
    warnings: string[];
  };
}

export type MapCreatorStyle = "stone" | "parchment" | "ink" | "inked-plan" | "engraved-atlas" | "blueprint";
export type MapCreatorColorGrade = "theme" | "natural" | "vibrant" | "gritty" | "moonlit" | "hellish";
export type MapCreatorShapeKind = "room" | "corridor";
export type MapCreatorPathFeature = "road" | "river" | "ridge";
export type MapCreatorBiome = "meadow" | "forest" | "marsh" | "desert" | "snowfield" | "highland" | "ash-wastes" | "coast" | "water";
export type MapCreatorGeographyRole = "ocean" | "continent" | "island" | "lake" | "basin";
export type MapCreatorWorldTemplate = "volcano" | "high-island" | "low-island" | "continents" | "archipelago" | "atoll" | "inland-sea" | "peninsula" | "pangaea" | "isthmus" | "shattered" | "taklamakan" | "old-world" | "fractious" | "epic-chronicle";
export type MapCreatorGridType = "square" | "hex";
export type MapCreatorDistanceUnit = "ft" | "m" | "mi" | "km";
export type MapCreatorDistributionMode = "scatter" | "boundary" | "path" | "baked";
export type MapCreatorLayout = "dungeon" | "megadungeon" | "cavern" | "underdark" | "manor" | "settlement" | "city-district" | "battlefield" | "wetlands" | "frozen-coast" | "desert" | "radial-temple" | "regional-landmark" | "frontier-region" | "island-realm" | "world-atlas";
export type MapCreatorTheme = "castle" | "crypt" | "tavern" | "cavern" | "wilderness" | "village" | "manor" | "volcanic" | "infernal" | "swamp" | "frozen" | "desert" | "temple";
export type MapCreatorFloorMaterial = "flagstone" | "cobblestone" | "wood" | "dirt" | "cave-earth" | "cave-rock" | "grass" | "marble" | "water" | "tile" | "lava" | "ice" | "sand" | "scorched-stone";
export type MapCreatorWallMaterial = "cut-stone" | "rough-stone" | "brick" | "timber" | "cave-rock" | "fortification" | "cliff" | "infernal-rock";
export type MapCreatorEnvironmentMaterial = "grass" | "earth" | "rock" | "moss" | "swamp" | "snow" | "sand" | "ash";
export type MapCreatorDressingDensity = "none" | "light" | "rich";
export type MapCreatorRoomPurpose = "bedchamber" | "common-room" | "library" | "armory" | "shrine" | "crypt" | "storage" | "workshop" | "courtyard" | "camp" | "natural" | "empty";
export type MapCreatorDecorationKind = "rug" | "table" | "barrel" | "crate" | "sarcophagus" | "brazier" | "tree" | "rock" | "bones" | "bed" | "bookshelf" | "bench" | "chest" | "rubble" | "cobweb" | "pillar" | "altar" | "reeds" | "lilypad" | "tent" | "statue" | "well" | "snowdrift" | "mast" | "hearth" | "desk" | "wardrobe" | "weapon-rack" | "fountain" | "stalagmite" | "mushroom" | "shrub" | "cart" | "market-stall" | "cactus" | "ice-spike" | MapCreatorGeneratedDecorationKind;
export type MapCreatorLayerKind = "terrain" | "structures" | "props";

export interface MapCreatorLayerState {
  id: MapCreatorLayerKind;
  visible: boolean;
  locked: boolean;
}

export interface MapCreatorGroup {
  id: Id;
  name: string;
  elementIds: Id[];
}

export interface MapCreatorEnhancements {
  grass: boolean;
  water: boolean;
  /** Legacy master switch retained for maps authored before the controls were split. */
  animatedProps?: boolean;
  trees?: boolean;
  fire?: "off" | "campfire" | "volumetric-fire" | "volume-fire";
}

/** Deterministic controls for editable world-atlas generation. */
export interface MapCreatorWorldSettings {
  template: MapCreatorWorldTemplate;
  continentCount: number;
  waterCoverage: number;
  islandDensity: number;
  coastRoughness: number;
  lakeCount: number;
  mountainDensity: number;
  forestCoverage: number;
  riverCount: number;
  settlementDensity: number;
}

export interface MapCreatorDecoration {
  id: Id;
  kind: MapCreatorDecorationKind;
  position: Point;
  size: number;
  rotation: number;
  roomId: Id;
}

/** Cartographic text that remains editable in grid space and survives export. */
export interface MapCreatorAnnotation {
  id: Id;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  fontSize: number;
  color: string;
  align?: "left" | "center" | "right";
  style?: "label" | "title" | "note";
}

export interface MapCreatorShape {
  id: Id;
  kind: MapCreatorShapeKind;
  x: number;
  y: number;
  width: number;
  height: number;
  /** Optional absolute grid cells for one connected irregular room footprint. */
  cells?: Point[];
  /** Exact vector outline for polygonal and radial architecture. */
  polygon?: Point[];
  /** Editable semantic centerline for roads and rivers. */
  path?: Point[];
  pathWidth?: number;
  feature?: MapCreatorPathFeature;
  /** Atlas-scale meaning used by coast rendering, layers, and editable generation. */
  geographyRole?: MapCreatorGeographyRole;
  /** Optional semantic outdoor terrain preset. The painted vector remains fully editable. */
  biome?: MapCreatorBiome;
  /** Portable material opacity and transition width for non-structural terrain. */
  terrainOpacity?: number;
  terrainBlend?: number;
  organic?: boolean;
  /** Terrain and roads can carry art without producing occluding walls. */
  wallMode?: "boundary" | "none";
  /** Optional generator-specific dressing. An empty list intentionally leaves this shape clear. */
  decorationKinds?: MapCreatorDecorationKind[];
  /** Non-destructive, deterministic object distribution attached to this plot or path. */
  decorationMode?: MapCreatorDistributionMode;
  decorationDensity?: number;
  decorationSizeMin?: number;
  decorationSizeMax?: number;
  decorationRandomRotation?: boolean;
  decorationSeed?: number;
  floorMaterial?: MapCreatorFloorMaterial;
  waterType?: WaterType;
  waterMovement?: number;
  waterOpacity?: number;
  waterFlowDirection?: number;
  grassDensity?: number;
  grassBladeSize?: number;
  /** Artwork-only transform for the tiled surface texture. Geometry stays unchanged. */
  textureScale?: number;
  textureRotation?: number;
  /** Visual height in grid-space levels. Geometry remains on its authored footprint. */
  elevation?: number;
  /** Persistent semantic furnishing preset. It changes dressing, never walls or sight geometry. */
  roomPurpose?: MapCreatorRoomPurpose;
}

/** Editable vector source retained in the DM workspace and TBLTOP's private DD2VTT extension; never sent to the Player Display. */
export interface MapCreatorDocument {
  version: 1;
  name: string;
  width: number;
  height: number;
  style: MapCreatorStyle;
  /** Non-destructive map-wide art direction shared by editor, PNG, DD2VTT, and campaign playback. */
  colorGrade?: MapCreatorColorGrade;
  colorGradeStrength?: number;
  layout?: MapCreatorLayout;
  /** Optional world-atlas recipe. Older sources use professional defaults. */
  worldSettings?: MapCreatorWorldSettings;
  theme?: MapCreatorTheme;
  defaultFloorMaterial?: MapCreatorFloorMaterial;
  corridorFloorMaterial?: MapCreatorFloorMaterial;
  wallMaterial?: MapCreatorWallMaterial;
  environmentMaterial?: MapCreatorEnvironmentMaterial;
  dressingDensity?: MapCreatorDressingDensity;
  /** Optional TBLTOP-only live layers. Portable exports always retain static fallback art. */
  enhancements?: MapCreatorEnhancements;
  /** Player-facing atmosphere applied when this map becomes a campaign scene. */
  timeOfDay?: TimeOfDay;
  atmosphericFog?: AtmosphericFogLevel;
  rain?: RainLevel;
  lightning?: LightningLevel;
  snow?: SnowLevel;
  snowflakeSize?: SnowflakeSize;
  windSpeed?: WindSpeed;
  /** Overrides the selected theme's ambient light when explicitly authored. */
  ambientLight?: number;
  gridVisible: boolean;
  /** Portable grid styling used by the editor, baked preview, and Player Display. */
  gridType?: MapCreatorGridType;
  gridSize?: number;
  /** Align object transforms to the authored grid when enabled. */
  snapToGrid?: boolean;
  gridColor?: string;
  gridOpacity?: number;
  /** Non-semantic cartographic furniture included in the editor and portable image. */
  compassVisible?: boolean;
  scaleBarVisible?: boolean;
  /** Authored legend value for atlas, regional, fantasy, or real-world maps. */
  scaleBarDistance?: number;
  scaleBarUnit?: MapCreatorDistanceUnit;
  /** One coherent outdoor key light used by portable artwork and editor previews. */
  sunEnabled?: boolean;
  sunDirection?: number;
  sunLength?: number;
  sunStrength?: number;
  seed: string;
  shapes: MapCreatorShape[];
  /** Explicit DM-placed props. Procedural dressing remains derived from the seed. */
  decorations?: MapCreatorDecoration[];
  annotations?: MapCreatorAnnotation[];
  /** Private editor organization retained for re-editing and never projected to players. */
  layers?: MapCreatorLayerState[];
  groups?: MapCreatorGroup[];
  manualWalls: Segment[];
  /** Imported object occluders retained separately from sight-blocking walls. */
  objectWalls?: Segment[];
  portals: Portal[];
  createdAt: number;
  updatedAt: number;
}

export interface CharacterSheetStat {
  id: Id;
  label: string;
  value: number;
}

export interface CharacterSheetTrack {
  id: Id;
  label: string;
  current: number;
  max: number;
}

/** Optional, campaign-local DM record. It is never included in Player Display snapshots. */
export interface CharacterSheet {
  id: Id;
  kind: CharacterSheetKind;
  profileId: CharacterSheetProfileId;
  name: string;
  playerName: string;
  pronouns: string;
  origin: string;
  path: string;
  rank: string;
  stats: CharacterSheetStat[];
  tracks: CharacterSheetTrack[];
  skills: string;
  abilities: string;
  inventory: string;
  conditions: string;
  notes: string;
  createdAt: number;
  updatedAt: number;
}

export type PlayingCardSuit = "clubs" | "diamonds" | "hearts" | "spades";
export type PlayingCardRank = "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "J" | "Q" | "K" | "A";

export interface PlayingCard {
  id: Id;
  suit: PlayingCardSuit;
  rank: PlayingCardRank;
}

export interface PokerSeat {
  id: Id;
  name: string;
  color: string;
  edge: TableEdge;
  slot: HeroSeatSlot;
  chips: number;
  committedChips: number;
  folded: boolean;
  hand: PlayingCard[];
}

export interface PokerTableCard {
  id: Id;
  card: PlayingCard;
  sourceSeatId?: Id;
  x: number;
  y: number;
  rotation: number;
  thrownAt: number;
  /** Measured phone gesture; absent for a dealer/default throw. */
  throwProfile?: PokerChipThrowProfile;
}

export interface PokerTableChip {
  id: Id;
  seatId: Id;
  amount: number;
  x: number;
  y: number;
  thrownAt: number;
  throwProfile?: PokerChipThrowProfile;
  placements?: PokerChipPlacement[];
}

export interface PokerChipPlacement {
  /** Normalized table position, independent of projector resolution. */
  x: number;
  y: number;
  stackLevel: number;
  rotation: number;
  tiltX: number;
  tiltY: number;
  motion?: { fromX: number; fromY: number; fromStackLevel: number; fromRotation: number; fromTiltX: number; fromTiltY: number; startedAt: number; reason?: "collision" | "auto-stack" | "manual" };
}

export interface PokerChipThrowProfile {
  /** Gesture time from touch-down to release. */
  durationMs: number;
  /** Normalized release speed, where 1 is a decisive phone swipe. */
  speed: number;
  /** Signed path curvature: negative bends left, positive bends right. */
  curve: number;
  /** Signed sideways release velocity in the player's local view. */
  releaseX: number;
  /** Upward release velocity in the player's local view. */
  releaseY: number;
}

export type PokerBlindMarkerKind = "small" | "big";

/** Public, normalized table placement for a physical blind marker disc. */
export interface PokerBlindMarker {
  kind: PokerBlindMarkerKind;
  seatId?: Id;
  x: number;
  y: number;
  rotation: number;
  movedAt: number;
  /** Previous pose retained long enough for the Player Display rigid-body glide. */
  motion?: { fromX: number; fromY: number; fromRotation: number; startedAt: number };
}

export interface PokerPayoutChip {
  id: Id;
  recipientSeatId: Id;
  recipientIndex: number;
  recipientChipIndex: number;
  value: number;
  source: PokerChipPlacement;
}

export interface PokerPayoutAnimation {
  id: Id;
  startedAt: number;
  durationMs: number;
  shares: Array<{ seatId: Id; amount: number }>;
  chips: PokerPayoutChip[];
}

export interface PokerFoldAnimation {
  id: Id;
  seatId: Id;
  /** Count only: Player Display never receives the folded card identities. */
  cardCount: number;
  startedAt: number;
  durationMs: number;
}

export type PokerTableStyleId = "casino-emerald" | "midnight-walnut" | "crimson-club" | "arctic-neon" | "royal-violet" | "jade-neon";
export type PokerTableDisplayMode = "table" | "mat-only";

export interface PokerState {
  deck: PlayingCard[];
  /** Folded cards retained privately until the next shuffle. */
  muck?: PlayingCard[];
  seats: PokerSeat[];
  tableCards: PokerTableCard[];
  tableChips: PokerTableChip[];
  pot: number;
  chipLayoutMode?: "loose" | "stacked" | "manual";
  tableStyle?: PokerTableStyleId;
  tableDisplayMode?: PokerTableDisplayMode;
  /** Player Display edge lighting from -100 (lighter) to 100 (darker). */
  tableEdgeShade?: number;
  /** Pending dealer payout; banks are credited only after its glide completes. */
  payoutAnimation?: PokerPayoutAnimation;
  /** Count-only metadata for the face-down glide to the dealer muck. */
  foldAnimation?: PokerFoldAnimation;
  /** Durable count-only event so a late Player Display still performs the fold. */
  lastFoldAnimation?: PokerFoldAnimation;
  dealerSeatId?: Id;
  blindMarkers?: Record<PokerBlindMarkerKind, PokerBlindMarker>;
  handNumber: number;
  updatedAt: number;
}

export interface PlayerPokerState {
  /** Present on player-safe snapshots. Optional only so generic non-poker preview fixtures remain assignable. */
  deckCount?: number;
  muckCount?: number;
  seats: Array<Omit<PokerSeat, "hand"> & { handCount?: number }>;
  tableCards: PokerTableCard[];
  tableChips: PokerTableChip[];
  pot: number;
  chipLayoutMode?: PokerState["chipLayoutMode"];
  tableStyle?: PokerTableStyleId;
  tableDisplayMode?: PokerTableDisplayMode;
  tableEdgeShade?: number;
  payoutAnimation?: PokerPayoutAnimation;
  foldAnimation?: PokerFoldAnimation;
  lastFoldAnimation?: PokerFoldAnimation;
  dealerSeatId?: Id;
  blindMarkers?: Record<PokerBlindMarkerKind, PokerBlindMarker>;
  handNumber: number;
  updatedAt: number;
}

export type BroadsideOrientation = "horizontal" | "vertical";
export type BroadsidePhase = "placement" | "battle" | "finished";
export type BroadsideShotResult = "miss" | "hit" | "sunk";

export interface BroadsideShip {
  id: Id;
  name: string;
  size: number;
  x: number;
  y: number;
  orientation: BroadsideOrientation;
  hits: string[];
}

export interface BroadsidePlayer {
  id: Id;
  name: string;
  color: string;
  ships: BroadsideShip[];
  ready: boolean;
}

export interface BroadsideShot {
  id: Id;
  playerId: Id;
  targetPlayerId: Id;
  x: number;
  y: number;
  result: BroadsideShotResult;
  shipId?: Id;
  createdAt: number;
}

export interface BroadsideState {
  gridSize: number;
  players: BroadsidePlayer[];
  phase: BroadsidePhase;
  activePlayerId?: Id;
  winnerPlayerId?: Id;
  turnNumber: number;
  shots: BroadsideShot[];
  lastShot?: BroadsideShot;
  updatedAt: number;
}

export interface PlayerBroadsidePlayer extends Omit<BroadsidePlayer, "ships"> {
  remainingShips?: number;
  sunkShips?: BroadsideShip[];
}

export interface PlayerBroadsideState extends Omit<BroadsideState, "players"> {
  players: PlayerBroadsidePlayer[];
}

export interface Token {
  id: Id;
  sceneId: Id;
  heroId?: Id;
  type: TokenType;
  name: string;
  position: Point;
  size: number;
  rotation: number;
  color: string;
  heroRingScale?: number;
  assetId?: Id;
  effect?: "campfire" | "volumetric-fire" | "volume-fire" | "procedural-tree" | "fly-swarm" | "twirl";
  tree?: TreeSettings;
  twirl?: TwirlSettings;
  volumeFire?: VolumeFireSettings;
  playerVisible: boolean;
  visionEnabled: boolean;
  visionRadius: number;
  darkvision: boolean;
  layer?: TokenLayer;
  locked?: boolean;
  statusMarkers?: StatusMarker[];
  /** Independent optional DM sheet for NPC/enemy tokens. Hero-linked tokens use Hero.characterSheet. */
  characterSheet?: CharacterSheet;
}

export interface FogShape {
  id: Id;
  kind: "brush" | "rectangle" | "polygon" | "all";
  mode: "allow" | "conceal" | "hard-hide";
  points: Point[];
  radius?: number;
  createdAt: number;
}

export interface FogState {
  operations: FogShape[];
  undoCursor: number;
  explored: Point[][];
  allowAll: boolean;
  exploredMaskRef?: string;
}

export interface WaterRegion {
  id: Id;
  points: Point[];
  /** Multiple painted shapes can be grouped into one selectable water element. */
  polygons?: Point[][];
  /** Clean perimeter produced from the union of the painted brush footprint. */
  areaModel?: "brush-union";
  /** Brush center lines, parallel to polygons, used to direct river flow. */
  flowPaths?: Point[][];
  /** Painted cut-outs made with the Command/Ctrl water eraser brush. */
  erasePolygons?: Point[][];
  brushRadii?: number[];
  waterType?: WaterType;
  /** Global top-down flow direction in screen-space degrees: 0 east, 90 south. */
  flowDirection?: number;
  movement?: number;
  opacity?: number;
  createdAt: number;
  locked?: boolean;
}

export interface WeatherDryRegion {
  id: Id;
  points: Point[];
  /** Multiple painted shapes can be grouped into one selectable dry element. */
  polygons?: Point[][];
  /** Clean perimeter produced from the union of the painted brush footprint. */
  areaModel?: "brush-union";
  createdAt: number;
  locked?: boolean;
}

export interface MaterialRegion {
  id: Id;
  name: string;
  material: EnvironmentMaterial;
  points: Point[];
  polygons?: Point[][];
  /** Grass uses the same grouped brush-area model as water and dry regions. */
  areaModel?: "brush-union";
  reactivity: number;
  opacity: number;
  color?: string;
  grassDensity?: number;
  grassBladeSize?: number;
  soundAssetId?: Id;
  soundGain?: number;
  createdAt: number;
  locked?: boolean;
}

export interface PhysicalObject {
  id: Id;
  name: string;
  position: Point;
  shape: "ellipse" | "rectangle";
  widthInches: number;
  depthInches: number;
  heightInches: number;
  rotation: number;
  blocksProjection: boolean;
  blocksWeather: boolean;
  locked?: boolean;
}

export interface EnvironmentSoundBinding {
  assetId: Id;
  gain: number;
}

export type EnvironmentSoundBindings = Partial<Record<EnvironmentSoundEvent, EnvironmentSoundBinding>>;

export interface MapMeasurement {
  id: Id;
  a: Point;
  b: Point;
  color: string;
  playerVisible: boolean;
  createdAt: number;
}

export interface AreaTemplate {
  id: Id;
  shape: "circle" | "cone" | "rectangle";
  a: Point;
  b: Point;
  color: string;
  playerVisible: boolean;
  createdAt: number;
}

export interface MapPin {
  id: Id;
  position: Point;
  label: string;
  notes: string;
  color: string;
  playerVisible: boolean;
  createdAt: number;
}

export interface CameraBookmark {
  id: Id;
  name: string;
  center: Point;
  createdAt: number;
}

export interface TurnEntry {
  id: Id;
  tokenId?: Id;
  name: string;
  initiative: number;
  playerVisible: boolean;
}

export interface TurnTrackerState {
  enabled: boolean;
  round: number;
  activeIndex: number;
  entries: TurnEntry[];
}

export interface MapPing {
  id: Id;
  sceneId: Id;
  position: Point;
  color: string;
  label?: string;
  commandAt: number;
}

export interface SceneLayerState { visible: boolean; locked: boolean }
export type SceneLayerSettings = Partial<Record<SceneLayerKey, SceneLayerState>>;

export interface SceneFolder {
  id: Id;
  campaignId: Id;
  name: string;
  /** Saved with the campaign's folder record so the DM library reopens as arranged. */
  collapsed?: boolean;
  createdAt: number;
}

export interface ViewTransform {
  x: number;
  y: number;
  zoom: number;
  rotation: 0 | 90 | 180 | 270;
  mode: "fit" | "physical" | "free";
}

export interface PlayerViewState {
  center: Point;
}

/** DM-only working-camera state. It never changes the Player Display framing. */
export interface DmOverlayViewState {
  zoom: number;
  pan: Point;
}

export interface MediaState {
  playing: boolean;
  /** @deprecated Older backups may carry this. Autoplay is now a workspace preference. */
  autoplay?: boolean;
  currentTime: number;
  loop: boolean;
  volume: number;
  commandTimestamp: number;
}

/** A non-destructive tabletop grid rendered above an image or video scene. */
export type MediaGridType = "square" | "honeycomb";

export interface MediaGrid {
  type: MediaGridType;
  /** Number of grid cells across the media's native width. */
  cellsAcross: number;
  color: string;
  opacity: number;
}

export interface StoryCard {
  title: string;
  text: string;
  imageAssetId?: Id;
  dmNotes: string;
}

export interface Scene {
  id: Id;
  campaignId: Id;
  name: string;
  type: SceneType;
  folderId?: Id;
  archived?: boolean;
  layers?: SceneLayerSettings;
  assetId?: Id;
  background: string;
  fit: "contain" | "cover";
  view: ViewTransform;
  playerView?: PlayerViewState;
  gridInches: number;
  transitionMs: number;
  timeOfDay?: TimeOfDay;
  atmosphericFog?: AtmosphericFogLevel;
  rain?: RainLevel;
  lightning?: LightningLevel;
  snow?: SnowLevel;
  snowflakeSize?: SnowflakeSize;
  windSpeed?: WindSpeed;
  waterRegions?: WaterRegion[];
  weatherDryRegions?: WeatherDryRegion[];
  materialRegions?: MaterialRegion[];
  physicalObjects?: PhysicalObject[];
  environmentSounds?: EnvironmentSoundBindings;
  measurements?: MapMeasurement[];
  areaTemplates?: AreaTemplate[];
  mapPins?: MapPin[];
  cameraBookmarks?: CameraBookmark[];
  turnTracker?: TurnTrackerState;
  geometry?: MapGeometry;
  /** Vector source for maps authored in TBLTOP; never projected to Player Display. */
  mapCreator?: MapCreatorDocument;
  /** Enables the complete tabletop-map toolset on an image or video source. */
  playableMap?: boolean;
  /** Saved independently for each scene; only used by the DM overlay. */
  dmOverlayView?: DmOverlayViewState;
  tokens: Token[];
  initialTokenPositions?: Record<Id, Point>;
  fog: FogState;
  story?: StoryCard;
  /** Poker campaigns use the same scene/presentation revision pipeline as maps. */
  poker?: PokerState;
  /** Broadside Skirmish keeps private fleets in the campaign scene. */
  broadside?: BroadsideState;
  media?: MediaState;
  mediaGrid?: MediaGrid;
  createdAt: number;
  updatedAt: number;
}

export interface Asset {
  id: Id;
  campaignId: Id;
  name: string;
  kind: AssetKind;
  mime: string;
  size: number;
  hash: string;
  opfsPath: string;
  width?: number;
  height?: number;
  duration?: number;
  audioCategory?: AudioCategory;
  /** Folder within the asset's audio category. Audio folders are global like shared audio. */
  audioFolderId?: Id;
  createdAt: number;
}

export interface AudioTrackSettings {
  volume: number;
  loop: boolean;
}

export interface AudioMixerState {
  masterVolume: number;
  categoryVolumes: Record<AudioCategory, number>;
  tracks: Record<Id, AudioTrackSettings>;
}

export type AidmModel = "gpt-5.6-sol" | "gpt-5.6-terra" | "gpt-5.6-luna" | "gpt-5-mini" | "gpt-5-nano";
export type AidmVoice = "alloy" | "ash" | "ballad" | "coral" | "echo" | "fable" | "nova" | "onyx" | "sage" | "shimmer" | "verse" | "marin" | "cedar";
export type AidmLanguage = "en" | "fr" | "es" | "de" | "it" | "pt" | "ja" | "zh";
export type AidmCostMode = "full" | "balanced" | "saver";

export interface AidmMessage {
  id: Id;
  role: "dm" | "aidm";
  text: string;
  /** Links a player-facing part of this answer to the Speech ledger. */
  speechEntryId?: Id;
  createdAt: number;
}

export interface AidmNarrationEntry {
  id: Id;
  text: string;
  source: "aidm" | "dm";
  /** Durable locally generated narration audio. The linked asset is DM-only. */
  audioAssetId?: Id;
  audioVoice?: AidmVoice;
  audioTone?: string;
  audioGeneratedAt?: number;
  createdAt: number;
}

export type HeroPresence = "present" | "away";

/** A durable, DM-authored attendance change that AIDM should account for. */
export interface AidmPresenceEvent {
  id: Id;
  heroId: Id;
  heroName: string;
  presence: HeroPresence;
  createdAt: number;
}

export interface AidmCampaignState {
  tone: string;
  briefing: string;
  messages: AidmMessage[];
  narration: AidmNarrationEntry[];
  presenceEvents: AidmPresenceEvent[];
  updatedAt: number;
}

export interface Campaign {
  id: Id;
  name: string;
  /** Missing means the original tabletop campaign type for backward compatibility. */
  kind?: CampaignKind;
  activeSceneId?: Id;
  /** Private DM-only AI context and history. Never projected to the Player Display. */
  aidm?: AidmCampaignState;
  createdAt: number;
  updatedAt: number;
}

export interface Hero {
  id: Id;
  campaignId: Id;
  name: string;
  color: string;
  ringScale?: number;
  seatEdge?: TableEdge;
  seatSlot?: HeroSeatSlot;
  /** Whether this hero's player is currently at the table. */
  presence?: HeroPresence;
  /** Shared by every token linked to this campaign hero. */
  characterSheet?: CharacterSheet;
  createdAt: number;
  updatedAt: number;
}

export interface TokenPreset {
  id: Id;
  campaignId: Id;
  name: string;
  type: TokenType;
  color: string;
  size: number;
  playerVisible: boolean;
  visionEnabled: boolean;
  visionRadius: number;
  darkvision: boolean;
  createdAt: number;
}

export interface AudioPlaylist {
  id: Id;
  campaignId: Id;
  name: string;
  assetIds: Id[];
  repeat: boolean;
  createdAt: number;
}

export interface AudioFolder {
  id: Id;
  name: string;
  category: AudioCategory;
  createdAt: number;
}

export type CueAction = "scene" | "blackout" | "cover" | "time-of-day" | "weather" | "audio";

export interface CueStep {
  id: Id;
  atMs: number;
  action: CueAction;
  sceneId?: Id;
  enabled?: boolean;
  timeOfDay?: TimeOfDay;
  rain?: RainLevel;
  snow?: SnowLevel;
  atmosphericFog?: AtmosphericFogLevel;
  lightning?: LightningLevel;
  windSpeed?: WindSpeed;
  audioAssetId?: Id;
  audioCommand?: "play" | "stop";
}

export interface CueSequence {
  id: Id;
  campaignId: Id;
  name: string;
  steps: CueStep[];
  createdAt: number;
  updatedAt: number;
}

export interface CalibrationProfile {
  id: Id;
  name: string;
  width: number;
  height: number;
  devicePixelRatio: number;
  pixelsPerInch: number;
  updatedAt: number;
}

export interface DisplayViewportMetrics {
  width: number;
  height: number;
  devicePixelRatio: number;
  fullscreen: boolean;
}

export type DiceKind = "d4" | "d6" | "d8" | "d10" | "d12" | "d20" | "d100";
export type TableEdge = "north" | "south" | "east" | "west";

export interface ThrownDie {
  id: Id;
  kind: DiceKind;
  value: number;
  seed: number;
  target: Point;
  color: string;
}

export interface DiceRollState {
  id: Id;
  origin: TableEdge;
  /** `preload` is an invisible Player Display warm-up, never a dice result. */
  phase: "preload" | "throw" | "recall";
  commandAt: number;
  dice: ThrownDie[];
}

export interface CharacterActionEffect {
  id: Id;
  sceneId: Id;
  sourceTokenId: Id;
  targetTokenId: Id;
  kind: "test-cast" | "test2-cast" | "spell-cast" | "weapon-action" | "general-action" | "class-action";
  /** Stable visual preset identifier; omitted by older TEST/TEST2 records. */
  effectId?: string;
  label: string;
  startedAt: number;
  durationMs: number;
}

export type BossRevealCircleVariant = "runic" | "void" | "infernal";

export interface BossRevealEffect {
  id: Id;
  sceneId: Id;
  position: Point;
  title: string;
  subtitle?: string;
  color: string;
  /** Bundled music cue selected once by the DM so every display receives the same reveal. */
  musicTrackId?: string;
  /** Synchronized random reveal-circle and GPU particle family. */
  circleVariant?: BossRevealCircleVariant;
  /** Visual footprint in grid squares; the real miniature remains protected separately. */
  radiusSquares: number;
  startedAt: number;
  durationMs: number;
}

export type ArenaEffectKind = "hero-spotlight" | "ground-fracture" | "portal-abyss" | "fireball-impact" | "lightning-strike" | "death-victory";

export interface ArenaProjectionEffect {
  id: Id;
  sceneId: Id;
  position: Point;
  kind: ArenaEffectKind;
  title?: string;
  /** Bundled arena music selected once so every display uses the same cue. */
  musicTrackId?: string;
  color: string;
  radiusSquares: number;
  outcome?: "death" | "victory";
  startedAt: number;
  durationMs: number;
}

export interface PresentationState {
  revision: number;
  campaignId?: Id;
  activeSceneId?: Id;
  stagedSceneId?: Id;
  stagedReady: boolean;
  stagingError?: string;
  blackout: boolean;
  covered: boolean;
  sessionWaiting?: boolean;
  tokenPlacementMode?: boolean;
  showFitScreen?: boolean;
  overrideSceneId?: Id;
  pairingCode?: string;
  displayTokenHash?: string;
  displayConnected: boolean;
  diceRoll?: DiceRollState;
  characterAction?: CharacterActionEffect;
  bossReveal?: BossRevealEffect;
  arenaEffect?: ArenaProjectionEffect;
  ping?: MapPing;
  cuePlayback?: { sequenceId: Id; name: string; startedAt: number; stepIndex: number };
  lastUpdated: number;
}

export interface AppSettings {
  passcodeSalt?: string;
  passcodeHash?: string;
  storagePersistent: boolean;
  lastBackupAt?: number;
  backupReminderDismissedAt?: number;
  activeCampaignId?: Id;
  calibrationProfileId?: Id;
  displayViewport?: DisplayViewportMetrics;
  audioMixer?: AudioMixerState;
  dmTableEdge?: TableEdge;
  coverColor?: string;
  sceneFolders?: SceneFolder[];
  tokenPresets?: TokenPreset[];
  audioFolders?: AudioFolder[];
  /** Version of the repository-bundled global audio library already merged into this workspace. */
  bundledMediaVersion?: string;
  /** Bundled tracks deliberately removed by this user; upgrades must not restore them. */
  suppressedBundledMediaIds?: Id[];
  audioPlaylists?: AudioPlaylist[];
  cueSequences?: CueSequence[];
  aidmModel?: AidmModel;
  aidmVoice?: AidmVoice;
  /** Forces AIDM's adviser and player-facing narration responses to this language. */
  aidmLanguage?: AidmLanguage;
  /** Controls how much historical AIDM context is sent per request. */
  aidmCostMode?: AidmCostMode;
  /** Automatically creates persistent voice audio for new player-facing AIDM Speech entries. */
  aidmAutoNarrate?: boolean;
  /** Tracks adoption of the current narrator profile without overriding later user voice choices. */
  aidmNarratorProfileVersion?: number;
  projectionAwareness?: ProjectionAwarenessSettings;
  tokenGridSnapping?: TokenGridSnappingSettings;
  /** Start every playable video map when it is taken live. */
  playableVideoAutoplay?: boolean;
}

export interface TokenGridSnappingSettings {
  player: boolean;
  enemy: boolean;
  neutral: boolean;
}

export interface ProjectionAwarenessSettings {
  enabled: boolean;
  projectorEdge: TableEdge | "overhead";
  safetyMarginInches: number;
  spillPerHeight: number;
}

export interface WorkspaceState {
  schemaVersion: number;
  campaigns: Campaign[];
  heroes: Hero[];
  assets: Asset[];
  scenes: Scene[];
  calibrations: CalibrationProfile[];
  presentation: PresentationState;
  settings: AppSettings;
}

export interface PlayerAsset {
  id: Id;
  name: string;
  kind: AssetKind;
  mime: string;
  opfsPath: string;
  width?: number;
  height?: number;
  duration?: number;
}

export type PlayerToken = Omit<Token, "heroId" | "locked" | "characterSheet">;
export type PlayerPhysicalObject = Omit<PhysicalObject, "name" | "locked">;
export type PlayerMaterialRegion = Omit<MaterialRegion, "name" | "soundAssetId" | "soundGain" | "locked">;

export interface PlayerScene extends Omit<Scene, "story" | "tokens" | "initialTokenPositions" | "mapPins" | "turnTracker" | "folderId" | "archived" | "layers" | "materialRegions" | "physicalObjects" | "environmentSounds" | "mapCreator" | "poker" | "broadside"> {
  story?: Omit<StoryCard, "dmNotes">;
  tokens: PlayerToken[];
  mapPins?: Array<Omit<MapPin, "notes">>;
  turnTracker?: TurnTrackerState;
  layers?: Partial<Record<SceneLayerKey, { visible: boolean }>>;
  materialRegions?: PlayerMaterialRegion[];
  physicalObjects?: PlayerPhysicalObject[];
  poker?: PlayerPokerState;
  broadside?: PlayerBroadsideState;
}

export interface PresentationSnapshot {
  revision: number;
  scene?: PlayerScene;
  assets: PlayerAsset[];
  calibration?: CalibrationProfile;
  calibrationPreviewPixelsPerInch?: number;
  diceRoll?: DiceRollState;
  characterAction?: CharacterActionEffect;
  bossReveal?: BossRevealEffect;
  arenaEffect?: ArenaProjectionEffect;
  ping?: MapPing;
  blackout: boolean;
  covered: boolean;
  sessionWaiting: boolean;
  dmTableEdge?: TableEdge;
  coverColor?: string;
  projectionAwareness?: ProjectionAwarenessSettings;
  placementMode: boolean;
  preload: boolean;
  generatedAt: number;
}

export interface VisibilityRequest {
  requestId: string;
  bounds: { width: number; height: number };
  segments: Segment[];
  portals: Portal[];
  sources: Array<{ id: Id; position: Point; radius: number; kind?: "vision" | "light" }>;
}

export interface VisibilityResult {
  requestId: string;
  polygons: Array<{ sourceId: Id; points: Point[]; kind?: "vision" | "light" }>;
  elapsedMs: number;
}

export interface ImportLimits {
  maxFileBytes: number;
  /** Portable campaign archives contain many validated media files. */
  maxBackupBytes: number;
  maxPixels: number;
  maxDimension: number;
  maxGeometryPoints: number;
  maxPortalsLights: number;
}

export interface ImportedFile {
  sourceName: string;
  kind: "map" | "image" | "video" | "audio" | "backup";
  mime: string;
  warnings: string[];
  map?: MapGeometry;
  /** Validated local editable source embedded by TBLTOP's own DD2VTT exporter. */
  mapCreator?: MapCreatorDocument;
  embeddedImage?: Blob;
  /** A Foundry scene's local background file, resolved by the import tray. */
  linkedMediaName?: string;
  original?: File;
}

export const DEFAULT_LIMITS: ImportLimits = {
  maxFileBytes: 250 * 1024 * 1024,
  maxBackupBytes: 2 * 1024 * 1024 * 1024,
  maxPixels: 160_000_000,
  maxDimension: 20_000,
  maxGeometryPoints: 200_000,
  maxPortalsLights: 5_000,
};
